import { Drawer } from "@/components/ui/drawer";
import { useSellerCarts } from "@/queries/cart";
import { formatPrice } from "@/utils/money";
import { pluralCount } from "@/utils/text";
import { cn } from "@repo/design-system/utils";
import { XMark } from "@medusajs/icons";
import { IconButton, Kbd } from "@medusajs/ui";
import { Link } from "@tanstack/react-router";
import { CartContents } from "./CartSidebar/CartContents";

export interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The cart as an off-canvas slide-over, opened from the header cart button.
 *
 * This is where the cart lives between `md` and `2xl`: the rail is a
 * `2xl`-only pane, and on a laptop it was costing the results ~320px of the
 * width they most need. Below `md` the phone reaches the cart through the
 * sticky bar and the full cart page instead, so this is never mounted there.
 * See storefront#145 (P4).
 *
 * Renders the same {@link CartContents} as the rail, so the two surfaces can't
 * drift. Unlike the rail it does *not* hide itself when the cart is empty - the
 * buyer just asked to see it, and a drawer that opens onto nothing reads as a
 * bug.
 *
 * Composes the low-level `Drawer` directly rather than `DrawerPanel`: the
 * header needs the product-count/total row laid out space-between under the
 * title, which `DrawerPanel`'s plain-text description can't do, and
 * `CartContents` is told to skip its own summary row so the two don't repeat
 * the same numbers back to back.
 *
 * The header itself is hand-rolled rather than `Drawer.Header` too: that
 * component's title-stack sits beside the esc-hint/close-button column, so a
 * `justify-between` row inside it stops short of the drawer's right edge -
 * short of where the seller prices below it end. Rebuilding the row as a
 * sibling of the title (not a child squeezed next to the icons) lets it span
 * the full header width, and matching its `px-4` to `CartContents`' own is
 * what actually lines the total up with the prices underneath.
 *
 * The header lives *inside* the scrolling wrapper (sticky, not a separate
 * `Drawer.Body`) rather than above it. A vertical scrollbar takes its width
 * out of whichever box it belongs to: with the header outside the scroll
 * container, a long cart narrows the seller rows but not the header, and the
 * total drifts out of alignment with the prices below it the moment the list
 * needs to scroll. Sharing one scroll context means both narrow together.
 */
export function CartDrawer({ open, onOpenChange }: CartDrawerProps) {
  const { data } = useSellerCarts();
  const itemCount = data?.summary.total_items ?? 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <Drawer.Content aria-describedby={undefined}>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="sticky top-0 z-10 flex flex-col gap-y-1 border-b border-gray-100 bg-white px-4 py-4">
            <div className="flex items-start justify-between gap-x-4">
              <Drawer.Title className="text-xl font-bold text-gray-900">
                Cart
              </Drawer.Title>
              <div className="flex items-center gap-x-2">
                <Kbd>esc</Kbd>
                <Drawer.Close asChild>
                  <IconButton size="small" type="button" variant="transparent">
                    <XMark />
                  </IconButton>
                </Drawer.Close>
              </div>
            </div>
            {itemCount > 0 && (
              <div className="flex items-baseline justify-between gap-x-4">
                <span className="text-sm text-gray-600">
                  {pluralCount(itemCount, "product")}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatPrice(data?.summary.grand_total ?? 0)}
                </span>
              </div>
            )}
          </div>
          <CartContents hideSummary />
        </div>
        {itemCount > 0 && (
          <Drawer.Footer>
            <Link
              to="/cart"
              onClick={() => onOpenChange(false)}
              className={cn(
                "flex w-full items-center justify-center",
                "rounded-lg bg-purple-600 px-4 py-2.5",
                "text-sm font-medium text-white",
                "transition-colors hover:bg-purple-700"
              )}
            >
              View cart and checkout
            </Link>
          </Drawer.Footer>
        )}
      </Drawer.Content>
    </Drawer>
  );
}
